import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  Box,
} from "@chakra-ui/react";
import { CheckoutProgress, Pedido } from "../../utils/interfaces";
import { formatBRL } from "../../utils/currency";
import { Input } from "../../components/Form/Input";
import { Controller, useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import { BiSolidLike, BiSolidMinusCircle } from "react-icons/bi";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  formCartaoCredito,
  validationCartaoCredito,
} from "../../schemas/cartaoCredito";
import { useMutationCartao } from "../../hooks/pagamento/mutationCartao";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import { queryClient } from "../../libs/queryClient";
import Swal from "sweetalert2";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCheckoutStatus } from "../../hooks/pagamento/useCheckoutStatus";
import { CheckoutProgressPanel, CheckoutStayOnPageWarning } from "../../components/CheckoutProgress";
import { createCheckoutProgressOverride, mergeCheckoutProgress } from "../../utils/checkoutProgress";
import { useVisualCheckoutProgress } from "../../hooks/pagamento/useVisualCheckoutProgress";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pedido: Pedido;
  numberOfParcela: number;
  isPaymentExpired?: boolean;
}

const sanitizeCheckoutErrorMessage = (rawMessage?: string) => {
  const base = (rawMessage ?? 'Erro ao finalizar o pedido').trim();
  const cleaned = base.replace(/\s*-+\s*Request ID:.*/i, '').trim();

  if (/genericexception|buson|does not contain desired reserve|falha na confirmacao|confirmorderreserve|nao e possivel ler o retorno/i.test(cleaned.toLowerCase())) {
    return 'Não é possível confirmar uma ou mais poltronas.\nO pedido e o pagamento foram cancelados.';
  }

  return cleaned;
};


export function PagamentoCartao({ isOpen, onClose, pedido, numberOfParcela, isPaymentExpired = false }: Props) {
  const mutation = useMutationCartao(pedido.id);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [localProgress, setLocalProgress] = useState<ReturnType<typeof createCheckoutProgressOverride> | null>(null);
  const errorHandledRef = useRef(false);
  const completionHandledRef = useRef(false);
  const beforeUnloadHandlerRef = useRef<((event: BeforeUnloadEvent) => void) | null>(null);
  const checkoutProcessingAllowed = isOpen && isProcessingCheckout && !isPaymentExpired;
  const { data: checkoutProgress } = useCheckoutStatus(pedido.id, checkoutProcessingAllowed);
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<formCartaoCredito>({
    resolver: zodResolver(validationCartaoCredito),
    defaultValues: {
      parcelas: "1"
    }
  });

  const parcelas = watch('parcelas')

  const disablePageUnloadProtection = useCallback(() => {
    if (!beforeUnloadHandlerRef.current) {
      return;
    }

    window.removeEventListener("beforeunload", beforeUnloadHandlerRef.current);
    beforeUnloadHandlerRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    if (isProcessingCheckout) {
      return;
    }

    onClose();
  }, [isProcessingCheckout, onClose]);

  const getYearSelect = () => {
    const currentYear = new Date().getFullYear();
    const options = Array.from({ length: 15 }, (_, index) => ({
      value: currentYear + index,
      label: `${currentYear + index}`,
    }));
    return options;
  };

  const onConfirm = async (data: formCartaoCredito) => {
    if (isPaymentExpired) {
      return;
    }

    try {
      errorHandledRef.current = false;
      completionHandledRef.current = false;
      setIsProcessingCheckout(true);
      setLocalProgress(
        createCheckoutProgressOverride(
          pedido.id,
          "cartao",
          "registering_payment",
          "Registrando pagamento",
          "Estamos validando os dados do cartão e registrando a transação.",
        ),
      );
      await mutation.mutateAsync(data);
      setLocalProgress(
        createCheckoutProgressOverride(
          pedido.id,
          "cartao",
          "payment_registered",
          "Pagamento registrado",
          "Pagamento registrado com sucesso. Agora vamos confirmar o pedido.",
        ),
      );
    } catch (err: unknown) {
      setIsProcessingCheckout(false);
      setLocalProgress(null);
      const error = err as AxiosErrorResponse;

      await queryClient.invalidateQueries({ queryKey: ["pedido"], exact: false });

      const message = sanitizeCheckoutErrorMessage(
        error.response?.data?.data?.message ||
        error.response?.data?.message ||
        'Erro desconhecido'
      );
      const redirectTo = error.response?.data?.data?.redirect_to;

      if (redirectTo) {
        disablePageUnloadProtection();
        onClose();
        sessionStorage.setItem('gopass_redirect_error_message', message);
        window.location.href = redirectTo;
        return;
      }

      Swal.fire({
        title: 'Erro ao finalizar o pedido',
        icon: "error",
        position: "center",
        showConfirmButton: true,
        text: message,
      });

      onClose();
    }
  };

  useEffect(() => {
    if (!checkoutProgress || !isProcessingCheckout) {
      return;
    }

    if (checkoutProgress.status === "failed" && !errorHandledRef.current) {
      errorHandledRef.current = true;
      completionHandledRef.current = true;
      disablePageUnloadProtection();
      setIsProcessingCheckout(false);
      setLocalProgress(null);

      Swal.fire({
        title: 'Erro ao finalizar o pedido',
        icon: "error",
        position: "center",
        showConfirmButton: true,
        text: checkoutProgress.error_message || checkoutProgress.description || 'Erro ao finalizar o pedido',
      });

      onClose();
    }
  }, [checkoutProgress, isProcessingCheckout, onClose, pedido.id]);

  useEffect(() => {
    if (checkoutProgress?.step && !["waiting_payment", "registering_payment", "payment_registered"].includes(checkoutProgress.step)) {
      setLocalProgress(null);
    }
  }, [checkoutProgress?.step]);

  useEffect(() => {
    if (!isOpen) {
      setIsProcessingCheckout(false);
      setLocalProgress(null);
      errorHandledRef.current = false;
      completionHandledRef.current = false;
      disablePageUnloadProtection();
    }
  }, [disablePageUnloadProtection, isOpen]);

  useEffect(() => {
    if (!isProcessingCheckout) {
      disablePageUnloadProtection();
      return;
    }

    disablePageUnloadProtection();

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    beforeUnloadHandlerRef.current = handleBeforeUnload;
    window.addEventListener("beforeunload", handleBeforeUnload);

    return disablePageUnloadProtection;
  }, [disablePageUnloadProtection, isProcessingCheckout]);

  const idleProgress = createCheckoutProgressOverride(
    pedido.id,
    "cartao",
    "waiting_payment",
    "Aguardando pagamento",
    "Preencha os dados do cartão para registrar o pagamento e continuar.",
  );

  const handleCompleted = useCallback((progress: CheckoutProgress) => {
    if (completionHandledRef.current) {
      return;
    }

    completionHandledRef.current = true;
    disablePageUnloadProtection();
    setIsProcessingCheckout(false);
    setLocalProgress(null);
    const target = progress.redirect_to || `/finalizada/${pedido.id}`;
    window.location.assign(target);
  }, [disablePageUnloadProtection, pedido.id]);

  const effectiveProgress = useVisualCheckoutProgress({
    pedidoId: pedido.id,
    method: "cartao",
    remoteProgress: checkoutProgress,
    localProgress,
    idleProgress,
    enabled: checkoutProcessingAllowed,
    onCompleted: handleCompleted,
  }) ?? (isProcessingCheckout ? mergeCheckoutProgress(checkoutProgress, localProgress) : idleProgress);

  return (
    <Modal
      size={"5xl"}
      key={"pagamento-dinheiro"}
      closeOnOverlayClick={false}
      closeOnEsc={!isProcessingCheckout}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent bg={"#f4f4f4"} maxW="1100px" borderRadius="2xl">
        <ModalHeader>PAGAMENTO CARTÃO - DETALHES DO PAGAMENTO</ModalHeader>
        <ModalCloseButton isDisabled={isProcessingCheckout} />
        <ModalBody pb={6}>
          <Box mt={4} mb={isProcessingCheckout ? 6 : 4}>
            <CheckoutStayOnPageWarning />
          </Box>
          {!isProcessingCheckout && (
            <>
          <Flex
            my={4}
            alignItems={"center"}
            justifyContent={"space-between"}
            w={"100%"}
          >
            <Text fontSize={"lg"}>VALOR TOTAL DO PEDIDO</Text>
            <Flex direction={'column'} justifyContent={'end'}>
              <Text fontSize={"3xl"} fontWeight={"bold"} textAlign={"right"}>
                {formatBRL(Number(pedido.valor_total))}
              </Text>
              <Text fontSize={"xl"} fontWeight={"normal"} textAlign={"right"}>
                {parcelas} parcelas de {formatBRL(Number(pedido.valor_total) / Number(parcelas))}
              </Text>
            </Flex>
          </Flex>
          <Flex gap={4} flexDir={"column"}>
            <FormControl>
              <FormLabel>Parcelas:</FormLabel>
              <Select
                  borderRadius={"none"}
                  focusBorderColor="purple.900"
                  bgColor="white"
                  variant="filled"
                  borderColor={"black"}
                  borderWidth={"0.125rem"}
                  borderStyle="solid"
                  size={"lg"}
                  height={"3.167rem"}
                  maxWidth={"100%"}
                  _hover={{
                    backgroundColor: "white",
                    borderColor: "black",
                  }}
                  {...register("parcelas")}
                >
                  {Array.from({ length: numberOfParcela }, (_, i) => i + 1).map((item) => (
                    <option value={`${item}`}>{item}</option> 
                  ))}
                </Select>
            </FormControl>
            <FormControl isInvalid={!!errors.numero_cartao}>
              <FormLabel>Número do Cartão:</FormLabel>
              <Controller
                name="numero_cartao"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <InputMask
                    {...field}
                    mask="9999 9999 9999 9999"
                    placeholder="Número do cartão"
                    alwaysShowMask={false}
                  >
                    {(inputProps) => (
                      <Input
                        bg={"white"}
                        color={"black"}
                        type="text"
                        {...inputProps}
                        placeholder="Número do cartão"
                      />
                    )}
                  </InputMask>
                )}
              />
              {!!errors.numero_cartao && (
                <FormErrorMessage>
                  {errors.numero_cartao.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <Flex flexDir={"row"}>
              <FormControl isInvalid={!!errors.cvv_cartao}>
                <FormLabel>Cód. segurança:</FormLabel>
                <Input
                  {...register("cvv_cartao")}
                  type="text"
                  maxLength={4}
                  minLength={3}
                  error={errors.cvv_cartao}
                />
              </FormControl>
              <FormControl isInvalid={!!errors.mes_cartao}>
                <FormLabel>Mês</FormLabel>
                <Select
                  borderRadius={"none"}
                  focusBorderColor="purple.900"
                  bgColor="white"
                  variant="filled"
                  borderColor={"black"}
                  borderWidth={"0.125rem"}
                  borderStyle="solid"
                  size={"lg"}
                  height={"3.167rem"}
                  maxWidth={"100%"}
                  _hover={{
                    backgroundColor: "white",
                    borderColor: "black",
                  }}
                  {...register("mes_cartao")}
                >
                  <option selected>Mês</option>
                  <option value="01">Janeiro</option>
                  <option value="02">Fevereiro</option>
                  <option value="03">Março</option>
                  <option value="04">Abril</option>
                  <option value="05">Maio</option>
                  <option value="06">Junho</option>
                  <option value="07">Julho</option>
                  <option value="08">Agosto</option>
                  <option value="09">Setembro</option>
                  <option value="10">Outubro</option>
                  <option value="11">Novembro</option>
                  <option value="12">Dezembro</option>
                </Select>
              </FormControl>
              <FormControl isInvalid={!!errors.ano_cartao}>
                <FormLabel>Ano</FormLabel>
                <Select
                  borderRadius={"none"}
                  focusBorderColor="purple.900"
                  bgColor="white"
                  variant="filled"
                  borderColor={"black"}
                  borderWidth={"0.125rem"}
                  borderStyle="solid"
                  size={"lg"}
                  height={"3.167rem"}
                  maxWidth={"100%"}
                  _hover={{
                    backgroundColor: "white",
                    borderColor: "black",
                  }}
                  {...register("ano_cartao")}
                >
                  <option selected>Ano</option>
                  {getYearSelect().map((item) => (
                    <option key={`cartao-pay-${item.value}`} value={item.value}>{item.label}</option>
                  ))}
                </Select>
              </FormControl>
            </Flex>
            <FormControl isInvalid={!!errors.nome}>
              <FormLabel>Titular do Cartão:</FormLabel>
              <Input
                bg={"white"}
                color={"black"}
                type="text"
                {...register("nome")}
                placeholder="Nome do Titular"
                error={errors.nome}
              />
            </FormControl>
            <FormControl isInvalid={!!errors.cpf}>
              <FormLabel>CPF do Titular:</FormLabel>
              <Input
                bg={"white"}
                color={"black"}
                type="text"
                {...register("cpf")}
                placeholder="CPF do titular"
                error={errors.cpf}
              />
            </FormControl>
            <CheckoutProgressPanel progress={effectiveProgress} fallbackStep="waiting_payment" method="cartao" />
          </Flex>
            </>
          )}
          {isProcessingCheckout && (
            <CheckoutProgressPanel progress={effectiveProgress} fallbackStep="waiting_payment" method="cartao" />
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            bg={"#92d050"}
            size={"lg"}
            rightIcon={<BiSolidLike size={"30px"} />}
            colorScheme="blue"
            mr={3}
            onClick={handleSubmit(onConfirm)}
            isLoading={mutation.isPending}
            isDisabled={isPaymentExpired || isProcessingCheckout}
          >
            CONFIRMAR
          </Button>
          <Button
            bg={"#ff0000"}
            size={"lg"}
            color={"white"}
            rightIcon={<BiSolidMinusCircle size={"30px"} />}
            onClick={onClose}
            disabled={mutation.isPending || isProcessingCheckout}
          >
            CANCELAR
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
